/**
 * usePresenceReporter — reports the current user's presence status to the server.
 *
 * Combines three event-driven signals (no polling):
 *   1. visibilitychange — tab hidden → "away", tab visible → "online"
 *   2. Idle timeout      — 5 min of no input while tab is visible → "away"
 *   3. Background grace  — 90s after tab hidden → proactive disconnect
 *
 * The grace-period disconnect prevents browser throttling of background tabs
 * from breaking SignalR keep-alives, which causes spurious reconnect loops.
 * Quick tab switches (< 90s) keep the connection warm — no churn.
 *
 * Sends at most one SignalR message per transition: online↔away.
 * The "offline" status is handled by SignalR's OnDisconnectedAsync — not here.
 *
 * Also updates the local presence store immediately so the current user's own
 * avatar reflects the change without waiting for a server round-trip.
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  sendPresenceUpdate,
  idleDisconnectChat,
  reconnectChatAfterIdle,
  isChatConnected,
} from '../api/chatConnection';
import { updatePresence } from './presenceStore';
import { getCurrentUserGuid } from '../api/client';

/** Idle timeout before marking presence as "away" (5 minutes). */
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Grace period after tab is hidden before disconnecting SignalR (90 seconds).
 * Short enough to free resources promptly; long enough that quick tab switches
 * (checking email, Slack, etc.) don't cause connection churn.
 */
const BACKGROUND_GRACE_MS = 90 * 1000;

export interface UsePresenceReporterOptions {
  /** When true, the hook is a no-op (e.g. kiosk mode). */
  disabled?: boolean;
}

export function usePresenceReporter(options?: UsePresenceReporterOptions): void {
  const disabled = options?.disabled ?? false;

  // Track the last status we reported so we don't send duplicate messages
  const lastReportedRef = useRef<'online' | 'away'>('online');
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True when we proactively disconnected chat due to background grace expiring */
  const bgDisconnectedRef = useRef(false);

  const report = useCallback((status: 'online' | 'away') => {
    if (lastReportedRef.current === status) return;
    lastReportedRef.current = status;

    // Update local store immediately for responsive UI
    const userGuid = getCurrentUserGuid();
    if (userGuid) {
      updatePresence({
        userGuid,
        status: status === 'away' ? 'idle' : 'online',
      });
    }

    // Fire-and-forget to SignalR (only if still connected)
    if (isChatConnected()) {
      sendPresenceUpdate(status).catch((err) => {
        console.warn('[PresenceReporter] Failed to send presence update:', err);
      });
    }
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      report('away');
    }, IDLE_TIMEOUT_MS);
  }, [report, clearIdleTimer]);

  useEffect(() => {
    if (disabled) return;

    // ── Visibility change (primary signal) ──────────────────────────
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden → immediately mark away, start grace timer
        clearIdleTimer();
        report('away');

        // Start background grace timer — if the user doesn't come back
        // within BACKGROUND_GRACE_MS, proactively disconnect to avoid
        // browser throttling breaking SignalR keep-alives.
        clearGraceTimer();
        graceTimerRef.current = setTimeout(() => {
          console.debug('[PresenceReporter] Background grace expired — disconnecting chat hub');
          bgDisconnectedRef.current = true;
          idleDisconnectChat().catch(() => { /* ignore */ });
        }, BACKGROUND_GRACE_MS);
      } else {
        // Tab visible → cancel grace timer, reconnect if needed, mark online
        clearGraceTimer();

        if (bgDisconnectedRef.current) {
          // We proactively disconnected — reconnect now
          console.debug('[PresenceReporter] Tab visible — reconnecting chat hub');
          bgDisconnectedRef.current = false;
          reconnectChatAfterIdle()
            .then(() => {
              // Report online after reconnect so the server gets the message
              lastReportedRef.current = 'away'; // force re-send
              report('online');
            })
            .catch((err) => {
              console.warn('[PresenceReporter] Failed to reconnect chat hub:', err);
            });
        } else {
          report('online');
        }

        startIdleTimer();
      }
    };

    // ── User activity (resets idle timer) ────────────────────────────
    const handleActivity = () => {
      // Only matters when tab is visible (hidden tabs are already "away")
      if (document.hidden) return;

      // If we were idle, coming back to activity means "online"
      if (lastReportedRef.current === 'away') {
        report('online');
      }
      startIdleTimer();
    };

    const activityEvents: Array<keyof DocumentEventMap> = [
      'mousemove', 'keydown', 'scroll', 'touchstart',
    ];

    document.addEventListener('visibilitychange', handleVisibilityChange);
    for (const evt of activityEvents) {
      document.addEventListener(evt, handleActivity, { passive: true });
    }

    // Seed the current user's own presence in the local store immediately.
    // The server will also broadcast via PresenceBulkSync, but this ensures
    // the user's own avatar shows "online" without waiting for the round-trip.
    const selfGuid = getCurrentUserGuid();
    if (selfGuid) {
      updatePresence({ userGuid: selfGuid, status: 'online' });
    }

    // Start initial idle timer (user is active and tab is visible on mount)
    startIdleTimer();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      for (const evt of activityEvents) {
        document.removeEventListener(evt, handleActivity);
      }
      clearIdleTimer();
      clearGraceTimer();
    };
  }, [disabled, report, startIdleTimer, clearIdleTimer, clearGraceTimer]);
}
